import React, { Fragment, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "./UiLibraryComponent.module.css";
import SearchIcon from "Utils/Assets/SVGs/SearchIcon";
import { ISearchBarProps } from "../../../Model/UIComponents.model";
import { selectFlatIndex } from "Data/Search/SearchSelectors";
import { useSmartSearchDropdown } from "../../../Logic/Functions/Hooks/useSmartSearchDropdown";
import type { ISearchResult, SearchItemType } from "@model/Search.model";
import { RootState } from "Data/Store/Store";

const SEARCH_TYPES: SearchItemType[] = ["category", "product", "service"];


const SearchBar: React.FC<ISearchBarProps> = ({
  value,
  onChange,
  placeholder = "Search…",
  typesFilter = SEARCH_TYPES,
  emptyEnterPath,
  enableDropdown = true, 
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const allResults = useSelector(selectFlatIndex);
  const error = useSelector((s: RootState) => s.searchReducer.error);

  const resolvedPath = emptyEnterPath ?? location.pathname;

  const filteredResults = useMemo(
    () =>
      allResults.filter((r) =>
        (typesFilter as SearchItemType[]).includes(r.type)
      ),
    [allResults, typesFilter]
  );

  const typePlurals: Record<SearchItemType, string> = {
    category: "categories",
    product: "products",
    service: "services",
  };

  const listboxId = "searchbar-dropdown-listbox";

  const {
    groups,
    showDropdown,
    setOpen,
    active,
    setActive,
    listRef,
    onKeyDown,
  } = useSmartSearchDropdown({
    value,
    results: filteredResults,
    limit: 10,
    typesFilter,
    enableDropdown, 
  });

  const choose = (result: ISearchResult | null) => {
    if (!result) return;
    navigate(result.url);
    setOpen(false);
  };

  const shouldShowDropdown =
    enableDropdown && (showDropdown || !!error || (!!value && groups.length === 0));

  return (
    <div className={styles.searchBar} data-testid="searchbar-container">
      <input
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={shouldShowDropdown}
        aria-controls={listboxId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => enableDropdown && groups.length && setOpen(true)} 
        onKeyDown={(e) => {
          if (e.key === "Enter" && (groups.length === 0 || error)) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }

          onKeyDown(e, choose, (q) =>
            navigate({
              pathname: resolvedPath,
              search: `?q=${encodeURIComponent(q)}`,
            })
          );
        }}
        placeholder={placeholder}
        className={styles.searchInput}
        data-testid="searchbar-input"
      />

      <SearchIcon
        className={styles.searchicon}
        width={20}
        height={20}
        aria-hidden
        data-testid="searchbar-icon"
      />

      {shouldShowDropdown && (
        <ul
          id={listboxId}
          ref={listRef}
          className={styles.searchDropdown}
          role="listbox"
          aria-label="Search suggestions"
          data-testid="searchbar-dropdown"
        >
          {error && (
            <li
              className={styles.errorItem}
              role="alert"
              data-testid="searchbar-error"
            >
              {error}
            </li>
          )}

          {!error && groups.length === 0 && value && (
            <li
              className={styles.emptyItem}
              aria-disabled
              data-testid="searchbar-empty"
            >
              No matches found for “{value}”
            </li>
          )}

          {groups.map((group, groupIdx) => (
            <Fragment key={`group-${group.type}-${groupIdx}`}>
              <li
                className={styles.groupHeader}
                data-testid="searchbar-group-header"
              >
                <span
                  className={[
                    styles.groupBadge,
                    group.type === "category"
                      ? styles.badgeCategory
                      : group.type === "product"
                      ? styles.badgeProduct
                      : styles.badgeService,
                  ].join(" ")}
                >
                  {group.type}
                </span>
                <span className={styles.groupTitle}>
                  Matches in {typePlurals[group.type]}
                </span>
              </li>

              {group.items.map((result, itemIdx) => {
                const isActive =
                  !!active &&
                  active.groupIndex === groupIdx &&
                  active.itemIndex === itemIdx;
                const key = `g${groupIdx}-i${itemIdx}`;
                return (
                  <li
                    key={key}
                    data-key={key}
                    role="option"
                    aria-selected={isActive}
                    className={isActive ? styles.activeItem : styles.item}
                    onMouseEnter={() =>
                      setActive({ groupIndex: groupIdx, itemIndex: itemIdx })
                    }
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      choose(result);
                    }}
                    data-testid="searchbar-item"
                  >
                    <span className={styles.itemLabel}>{result.label}</span>
                  </li>
                );
              })}
            </Fragment>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SearchBar;
